import { format } from 'date-fns';
import { Circle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import type { RmaActivityEntry } from '../types';

interface RmaActivityTimelineProps {
  activity: RmaActivityEntry[];
  createdBy?: string | null;
}

function formatAction(action: string, t: ReturnType<typeof useTranslation>['t']): string {
  // Fallback = today's humanization, so unknown future actions render as before.
  const fallback = action
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
  return t(`rma.activity.action.${action}`, fallback);
}

// Activity `details` keys are DAO column slugs (shipFromName, cpServiceCode…)
// and update entries carry `{ fields: [...slugs] }` — render translated labels
// instead of dumping camelCase into the timeline. These English strings are the
// t() fallbacks; the real labels live in operations.json (rma.activity.field.*).
// Action names follow the same pattern via rma.activity.action.* (fallback =
// the humanized slug, e.g. label_generated -> "Label Generated").
const FIELD_LABELS: Record<string, string> = {
  fields: 'Fields',
  shipFromName: 'From name',
  shipFromCompany: 'From company',
  shipFromLine1: 'From address line 1',
  shipFromLine2: 'From address line 2',
  shipFromCity: 'From city',
  shipFromProvince: 'From province',
  shipFromPostalCode: 'From postal code',
  carrier: 'Carrier',
  cpServiceCode: 'Canada Post service',
  cpTrackingPin: 'Canada Post tracking PIN',
  cpQrCode: 'Canada Post QR code',
  pcShipmentNumber: 'Planète Courrier shipment no.',
  pcServiceName: 'Planète Courrier service',
  pcPriceTotal: 'Planète Courrier price',
  rmaNumber: 'RMA number',
  quoteNumber: 'Quote number',
  trackingPin: 'Tracking PIN',
  serviceCode: 'Service code',
  sku: 'SKU',
  quantity: 'Quantity',
  weight: 'Weight',
  reason: 'Reason',
  status: 'Status',
  from: 'From status',
  to: 'To status',
};

function humanize(key: string): string {
  return key.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/^./, (c) => c.toUpperCase());
}

function fieldLabel(key: string, t: ReturnType<typeof useTranslation>['t']): string {
  return t(`rma.activity.field.${key}`, FIELD_LABELS[key] ?? humanize(key));
}

export function RmaActivityTimeline({ activity, createdBy }: RmaActivityTimelineProps) {
  const { t } = useTranslation('operations');
  // Most recent first
  const sorted = [...activity].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base font-medium">{t('rma.activity.title', 'Activity')}</CardTitle>
        {createdBy && (
          <p className="text-xs text-muted-foreground">
            {t('rma.activity.createdBy', 'Created by {{name}}', { name: createdBy })}
          </p>
        )}
      </CardHeader>
      <CardContent>
        {sorted.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('rma.activity.empty', 'No activity recorded.')}</p>
        ) : (
          <div className="relative space-y-0">
            {sorted.map((entry, index) => (
              <div key={entry.id} className="flex gap-3 pb-4 last:pb-0">
                {/* Timeline line + dot */}
                <div className="flex flex-col items-center">
                  <Circle className="h-2.5 w-2.5 fill-primary text-primary shrink-0 mt-1.5" />
                  {index < sorted.length - 1 && (
                    <div className="w-px flex-1 bg-border" />
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0 space-y-0.5">
                  <p className="text-sm font-medium">{formatAction(entry.action, t)}</p>
                  {entry.details && Object.keys(entry.details).length > 0 && (
                    <p className="text-xs text-muted-foreground break-words">
                      {Object.entries(entry.details)
                        .map(([key, value]) => {
                          // `fields` values are column slugs too — label them as well.
                          const v = Array.isArray(value)
                            ? value.map((x) => fieldLabel(String(x), t)).join(', ')
                            : String(value);
                          return `${fieldLabel(key, t)}: ${v}`;
                        })
                        .join(' · ')}
                    </p>
                  )}
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span>{format(new Date(entry.createdAt), 'MMM d, yyyy h:mm a')}</span>
                    {entry.performedBy && (
                      <>
                        <span>&middot;</span>
                        <span>{entry.performedBy}</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
